import React, { useEffect, useRef } from 'react'
import {
  applyPasswordManagerIgnoreToSubtree,
  PASSWORD_MANAGER_IGNORE_CONTAINER_PROPS,
} from '../../utils/passwordManagerIgnore'

type PasswordManagerGuardProps = {
  children: React.ReactNode
  enabled?: boolean
  'data-testid'?: string
}

let observerRefCount = 0
let sharedObserver: MutationObserver | null = null
let processingScheduled = false
let pendingNodes: HTMLElement[] = []
let idleCallbackHandle: number | null = null
let animationFrameHandle: number | null = null

const processPendingNodes = () => {
  idleCallbackHandle = null
  animationFrameHandle = null
  processingScheduled = false

  const nodes = pendingNodes
  pendingNodes = []
  nodes.forEach(applyPasswordManagerIgnoreToSubtree)
}

const scheduleProcessPendingNodes = () => {
  if (processingScheduled) {
    return
  }

  processingScheduled = true

  if (typeof requestIdleCallback !== 'undefined') {
    idleCallbackHandle = requestIdleCallback(processPendingNodes, {
      timeout: 300,
    })
    return
  }

  animationFrameHandle = requestAnimationFrame(processPendingNodes)
}

const cancelScheduledProcessing = () => {
  if (
    idleCallbackHandle !== null &&
    typeof cancelIdleCallback !== 'undefined'
  ) {
    cancelIdleCallback(idleCallbackHandle)
    idleCallbackHandle = null
  }

  if (animationFrameHandle !== null) {
    cancelAnimationFrame(animationFrameHandle)
    animationFrameHandle = null
  }

  processingScheduled = false
  pendingNodes = []
}

const getSharedObserver = (): MutationObserver => {
  if (!sharedObserver) {
    sharedObserver = new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        mutation.addedNodes.forEach(node => {
          if (node instanceof HTMLElement) {
            pendingNodes.push(node)
          }
        })
      })

      scheduleProcessPendingNodes()
    })
    sharedObserver.observe(document.body, { childList: true, subtree: true })
  }

  return sharedObserver
}

const releaseSharedObserver = () => {
  observerRefCount--
  if (observerRefCount <= 0) {
    observerRefCount = 0
    sharedObserver?.disconnect()
    sharedObserver = null
    cancelScheduledProcessing()
  }
}

/**
 * Prevents password-manager extensions (e.g. 1Password) from scanning form fields.
 * Needed in embedded contexts (Salesforce Canvas iframe) where extensions cause
 * flickering overlays and UI freezes on dynamic Formik forms.
 */
export const PasswordManagerGuard: React.FC<PasswordManagerGuardProps> = ({
  children,
  enabled = true,
  'data-testid': dataTestId = 'password-manager-guard',
}) => {
  const rootRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!enabled || typeof document === 'undefined') {
      return
    }

    const root = rootRef.current
    if (root) {
      applyPasswordManagerIgnoreToSubtree(root)
    }

    // Observe document.body so MUI portaled inputs (menus, dialogs) are covered too.
    applyPasswordManagerIgnoreToSubtree(document.body)

    observerRefCount++
    getSharedObserver()

    return () => {
      releaseSharedObserver()
    }
  }, [enabled])

  return (
    <div
      ref={rootRef}
      data-testid={dataTestId}
      {...(enabled
        ? {
            'data-1p-ignore':
              PASSWORD_MANAGER_IGNORE_CONTAINER_PROPS['data-1p-ignore'],
            'data-lpignore':
              PASSWORD_MANAGER_IGNORE_CONTAINER_PROPS['data-lpignore'],
            'data-bwignore':
              PASSWORD_MANAGER_IGNORE_CONTAINER_PROPS['data-bwignore'],
          }
        : {})}
    >
      {children}
    </div>
  )
}
